/**
 * Attachment helpers shared across all Caliu clients.
 *
 * A non-image file attachment is stored in markdown as a plain link whose href
 * points at the canonical download endpoint:
 *
 *   [filename.pdf](https://api.caliuapp.com/attachments/<id>/download)
 *
 * Images keep the image form `![alt](url)`. The single discriminator between an
 * image and a file attachment that both point at `/attachments/.../download` is
 * the leading `!`. Clients "upgrade" matching links into a file chip.
 */

/**
 * Matches the path of an attachment download URL, anchored at the end (allowing
 * an optional query string or fragment). Intentionally loose on the origin so
 * it works across `api.caliuapp.com`, `localhost:8787`, and any future host.
 */
export const ATTACHMENT_URL_PATTERN = /\/attachments\/[A-Za-z0-9_-]+\/download(?:[?#]|$)/

/**
 * True when `href` is a Caliu attachment download URL. Used to upgrade a plain
 * markdown link into a file chip in every client (PWA editor, web viewer, Swift).
 */
export function isAttachmentDownloadUrl(href: string | null | undefined): boolean {
  if (!href) return false
  return ATTACHMENT_URL_PATTERN.test(href)
}

/**
 * A markdown link that is NOT an image (negative lookbehind on `!`), capturing
 * its URL. Used to find plain `[name](url)` links so attachment download links
 * can be removed from derived title/preview text.
 */
const ATTACHMENT_MD_LINK_RE =
  /(?<!!)\[[^\]]*\]\(([^)\s]+)(?:\s+(?:"[^"]*"|'[^']*'))?\)/g

/**
 * Remove non-image file-attachment links (`[name](.../attachments/<id>/download)`)
 * from a string entirely — they render as chips in the note list, so their
 * filename must not count as title or preview content. Non-attachment links are
 * left untouched. Image syntax (`![alt](url)`) is skipped via the lookbehind, so
 * this is safe regardless of whether image removal ran first.
 *
 * Single source of truth for the web preview (`notePreviewLines`), the app title
 * derivation (`extractTitleFromContent`), and the worker title (`deriveTitle`).
 */
export function stripAttachmentDownloadLinks(text: string): string {
  return text.replace(ATTACHMENT_MD_LINK_RE, (match, url: string) =>
    isAttachmentDownloadUrl(url) ? '' : match,
  )
}

/**
 * Extract the attachment id from a download URL, or null if it isn't one.
 */
export function attachmentIdFromUrl(href: string | null | undefined): string | null {
  if (!href) return null
  const match = href.match(/\/attachments\/([A-Za-z0-9_-]+)\/download(?:[?#]|$)/)
  return match ? match[1]! : null
}

/**
 * Format a byte count into a short human-readable string (e.g. `1.4 MB`).
 * Returns an empty string for null/undefined/negative input so callers can
 * render "size unknown" by omission.
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes < 0 || !Number.isFinite(bytes)) return ''
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }
  // One decimal place, but drop a trailing `.0`.
  let rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10
  // Rounding can push a value onto the next unit boundary (e.g. 1023.96 KB
  // rounds to 1024, which should read as "1 MB").
  if (rounded >= 1024 && unitIndex < units.length - 1) {
    rounded = 1
    unitIndex += 1
  }
  return `${rounded} ${units[unitIndex]}`
}

/**
 * Semantic icon key for a file, derived from its extension (preferred) or MIME
 * type. Each client maps these keys to its own icon set — Lucide on the web,
 * SF Symbols on Swift — so the choice stays consistent across platforms.
 */
export type FileIconKey =
  | 'pdf'
  | 'archive'
  | 'audio'
  | 'video'
  | 'image'
  | 'spreadsheet'
  | 'presentation'
  | 'document'
  | 'code'
  | 'text'
  | 'file'

const EXTENSION_ICONS: Record<string, FileIconKey> = {
  pdf: 'pdf',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  tgz: 'archive',
  bz2: 'archive',
  mp3: 'audio',
  wav: 'audio',
  flac: 'audio',
  aac: 'audio',
  ogg: 'audio',
  m4a: 'audio',
  mp4: 'video',
  mov: 'video',
  avi: 'video',
  mkv: 'video',
  webm: 'video',
  m4v: 'video',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  heic: 'image',
  avif: 'image',
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  csv: 'spreadsheet',
  numbers: 'spreadsheet',
  ppt: 'presentation',
  pptx: 'presentation',
  key: 'presentation',
  doc: 'document',
  docx: 'document',
  odt: 'document',
  pages: 'document',
  rtf: 'document',
  js: 'code',
  ts: 'code',
  tsx: 'code',
  jsx: 'code',
  py: 'code',
  rb: 'code',
  go: 'code',
  rs: 'code',
  java: 'code',
  c: 'code',
  h: 'code',
  cpp: 'code',
  swift: 'code',
  json: 'code',
  html: 'code',
  css: 'code',
  sh: 'code',
  yml: 'code',
  yaml: 'code',
  xml: 'code',
  txt: 'text',
  md: 'text',
  markdown: 'text',
  log: 'text',
}

function iconFromMime(mime: string): FileIconKey | null {
  if (mime === 'application/pdf') return 'pdf'
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('video/')) return 'video'
  // CSV is `text/*` but is better represented as a spreadsheet — check before
  // the generic `text/` fallback below, which would otherwise shadow it.
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') {
    return 'spreadsheet'
  }
  if (mime.startsWith('text/')) return 'text'
  if (mime === 'application/zip' || mime.includes('compressed') || mime.includes('tar')) {
    return 'archive'
  }
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'presentation'
  if (mime.includes('word') || mime.includes('document')) return 'document'
  if (mime.includes('json') || mime.includes('javascript') || mime.includes('xml')) return 'code'
  return null
}

/**
 * Pick a semantic icon key for a file. Extension wins when recognized; falls
 * back to the MIME type, then to a generic `file` icon.
 */
export function fileIconForName(
  filename: string | null | undefined,
  mime?: string | null,
): FileIconKey {
  const name = (filename ?? '').toLowerCase()
  const dot = name.lastIndexOf('.')
  if (dot >= 0 && dot < name.length - 1) {
    const ext = name.slice(dot + 1)
    const byExt = EXTENSION_ICONS[ext]
    if (byExt) return byExt
  }
  if (mime) {
    const byMime = iconFromMime(mime.toLowerCase())
    if (byMime) return byMime
  }
  return 'file'
}
